import React, { useState, useRef, useCallback } from "react";
import { supabase } from "./supabaseClient";
import { recordUpload, BADGES } from "./Gamification";
import BodyFeedback from "./BodyFeedback";

const SUPABASE_ANON_KEY = "sb_publishable_gsHORs-OWooYG4WzhAIcHw_5D81FM84";

function extractFrames(videoFile, numFrames) {
  return new Promise(function(resolve, reject) {
    var video = document.createElement("video");
    var canvas = document.createElement("canvas");
    var ctx = canvas.getContext("2d");
    var frames = [];
    video.preload = "auto";
    video.muted = true;
    video.src = URL.createObjectURL(videoFile);
    video.onloadedmetadata = function() {
      canvas.width = 640;
      canvas.height = 360;
      var duration = video.duration;
      var interval = duration / (numFrames + 1);
      var captureFrame = function(time) {
        return new Promise(function(res) {
          video.currentTime = time;
          video.onseeked = function() {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            var base64 = canvas.toDataURL("image/jpeg", 0.7).split(",")[1];
            frames.push(base64);
            res();
          };
        });
      };
      var captureAll = async function() {
        for (var i = 1; i <= numFrames; i++) {
          await captureFrame(interval * i);
        }
        URL.revokeObjectURL(video.src);
        resolve(frames);
      };
      captureAll().catch(reject);
    };
    video.onerror = reject;
  });
}

function VideoUpload({ user, onStatsUpdate }) {
  const [video, setVideo] = useState(null);
  const [style, setStyle] = useState("orthodox");
  const [uploading, setUploading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [reward, setReward] = useState(null);
  const [progressMsg, setProgressMsg] = useState("");
  const [activeTab, setActiveTab] = useState("visual");
  const [mode, setMode] = useState("upload");
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [cameraReady, setCameraReady] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const MAX_SIZE_MB = 50;
  const MAX_RECORD_SECONDS = 60;

  const boxingStyles = [
    { value: "orthodox", label: "Orthodox", desc: "Traditional stance, left foot forward" },
    { value: "southpaw", label: "Southpaw", desc: "Right foot forward, mirror of orthodox" },
    { value: "philly_shell", label: "Philly Shell", desc: "Shoulder roll defensive style" },
    { value: "peek_a_boo", label: "Peek-a-Boo", desc: "Hands high, aggressive weaving" },
    { value: "soviet", label: "Soviet/Kronk", desc: "Upright stance, powerful combinations" },
    { value: "swarmer", label: "Swarmer", desc: "High pressure, inside fighting" },
  ];

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setCameraReady(true);
      setError(null);
    } catch (err) {
      setError("Camera access denied. Please allow camera access and try again.");
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(function(t) { t.stop(); });
      streamRef.current = null;
    }
    setCameraReady(false);
    setRecording(false);
    setRecordingTime(0);
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      setRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, []);

  const startRecording = useCallback(() => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    var mediaRecorder;
    try {
      mediaRecorder = new MediaRecorder(streamRef.current, { mimeType: "video/webm" });
    } catch(e) {
      mediaRecorder = new MediaRecorder(streamRef.current);
    }
    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.ondataavailable = function(e) {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    mediaRecorder.onstop = function() {
      var blob = new Blob(chunksRef.current, { type: "video/webm" });
      var file = new File([blob], "recording-" + Date.now() + ".webm", { type: "video/webm" });
      var url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setVideo(file);
      stopCamera();
    };
    mediaRecorder.start();
    setRecording(true);
    setRecordingTime(0);
    timerRef.current = setInterval(function() {
      setRecordingTime(function(prev) {
        if (prev >= MAX_RECORD_SECONDS - 1) {
          stopRecording();
          return prev;
        }
        return prev + 1;
      });
    }, 1000);
  }, [stopCamera, stopRecording]);

  const switchMode = useCallback(function(newMode) {
    stopCamera();
    setMode(newMode);
    setVideo(null);
    setPreviewUrl(null);
    setError(null);
    if (newMode === "record") {
      setTimeout(function() { startCamera(); }, 300);
    }
  }, [stopCamera, startCamera]);

  const handleUpload = async () => {
    if (!video) return;
    if (video.size > MAX_SIZE_MB * 1024 * 1024) {
      setError("Video is too large. Please keep it under 50MB.");
      return;
    }
    setUploading(true);
    setError(null);
    setFeedback(null);
    setAnalysis(null);
    setReward(null);
    try {
      setProgressMsg("Uploading video...");
      var fileName = user.id + "/" + Date.now() + "_" + video.name;
      var uploadResult = await supabase.storage.from("videos").upload(fileName, video);
      if (uploadResult.error) throw uploadResult.error;
      setProgressMsg("Extracting frames from your video...");
      var frames = await extractFrames(video, 5);
      setProgressMsg("AI Coach is analyzing your technique...");
      setFeedback("analyzing");
      var response = await fetch("https://ybcwbttitirdwbjtitsk.supabase.co/functions/v1/ai-feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + SUPABASE_ANON_KEY },
        body: JSON.stringify({ style: style, frames: frames })
      });
      var data = await response.json();
      if (data.feedback) {
        setFeedback(data.feedback);
        setAnalysis(data.analysis);
        setProgressMsg("");
        await supabase.from("feedback_history").insert({
          user_id: user.id,
          style: style,
          feedback: data.feedback,
          analysis: data.analysis || null
        });
        var result = await recordUpload(user.id);
        if (result) { setReward(result); if (onStatsUpdate) onStatsUpdate(); }
      } else { throw new Error(data.error || "No feedback received"); }
    } catch (err) { setError(err.message); setFeedback(null); setProgressMsg(""); }
    setUploading(false);
  };

  const resetAll = function() {
    setFeedback(null);
    setAnalysis(null);
    setVideo(null);
    setReward(null);
    setPreviewUrl(null);
    setError(null);
    if (mode === "record") startCamera();
  };

  return (
    <div style={{fontFamily:"Inter, sans-serif"}}>
      <style>{`
        @keyframes pulse-ring {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.8); opacity: 0; }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>

      <h2 style={{fontFamily:"Bebas Neue, sans-serif",fontSize:"2.5rem",letterSpacing:"2px",background:"linear-gradient(135deg, #e94560, #ff6b35)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",marginBottom:"25px"}}>ANALYZE YOUR TECHNIQUE</h2>

      {!feedback || feedback === "analyzing" ? (
        <>
          <div style={{display:"flex",gap:"8px",marginBottom:"25px"}}>
            <button onClick={function(){ switchMode("upload"); }} style={{flex:1,padding:"14px",borderRadius:"12px",cursor:"pointer",fontFamily:"Inter, sans-serif",fontSize:"0.95rem",fontWeight:"700",border: mode==="upload" ? "none" : "1px solid rgba(255,255,255,0.08)",background: mode==="upload" ? "linear-gradient(135deg, #e94560, #ff6b35)" : "rgba(255,255,255,0.03)",color: mode==="upload" ? "white" : "#666"}}>
              📁 Upload Video
            </button>
            <button onClick={function(){ switchMode("record"); }} style={{flex:1,padding:"14px",borderRadius:"12px",cursor:"pointer",fontFamily:"Inter, sans-serif",fontSize:"0.95rem",fontWeight:"700",border: mode==="record" ? "none" : "1px solid rgba(255,255,255,0.08)",background: mode==="record" ? "linear-gradient(135deg, #e94560, #ff6b35)" : "rgba(255,255,255,0.03)",color: mode==="record" ? "white" : "#666"}}>
              🎥 Record Live
            </button>
          </div>

          <div style={{background:"rgba(255,255,255,0.02)",padding:"20px",borderRadius:"16px",marginBottom:"20px",border:"1px solid rgba(255,255,255,0.06)"}}>
            <label style={{display:"block",marginBottom:"12px",color:"#888",fontSize:"0.8rem",textTransform:"uppercase",letterSpacing:"1px"}}>Select Your Boxing Style</label>
            <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:"8px"}}>
              {boxingStyles.map(function(s) {
                return (
                  <div key={s.value} onClick={function(){ setStyle(s.value); }} style={{padding:"12px",borderRadius:"10px",cursor:"pointer",border: style===s.value ? "1px solid rgba(233,69,96,0.6)" : "1px solid rgba(255,255,255,0.06)",background: style===s.value ? "linear-gradient(135deg, rgba(233,69,96,0.15), rgba(255,107,53,0.08))" : "rgba(255,255,255,0.02)",transition:"all 0.2s"}}>
                    <p style={{color: style===s.value ? "#e94560" : "white",margin:0,fontWeight:"600",fontSize:"0.85rem"}}>{s.label}</p>
                    <p style={{color:"#555",margin:"2px 0 0 0",fontSize:"0.7rem"}}>{s.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {mode === "upload" && (
            <div style={{background:"rgba(255,255,255,0.02)",padding:"25px",borderRadius:"16px",border:"1px solid rgba(255,255,255,0.06)"}}>
              {!video ? (
                <div style={{border:"2px dashed rgba(255,255,255,0.1)",borderRadius:"12px",padding:"40px",textAlign:"center",position:"relative",cursor:"pointer"}}>
                  <input type="file" accept="video/*" onChange={function(e){ setVideo(e.target.files[0]); }} style={{position:"absolute",inset:0,opacity:0,cursor:"pointer",width:"100%",height:"100%"}} />
                  <div style={{fontSize:"3rem",marginBottom:"12px"}}>📹</div>
                  <p style={{color:"#666",margin:0,fontSize:"0.95rem"}}>Click to upload your training video</p>
                  <p style={{color:"#444",margin:"8px 0 0 0",fontSize:"0.8rem"}}>MP4, MOV, AVI supported • Max 50MB • 30 seconds recommended</p>
                </div>
              ) : (
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:"2rem",marginBottom:"8px"}}>✅</div>
                  <p style={{color:"#e94560",margin:0,fontWeight:"600"}}>{video.name}</p>
                  <p style={{color:"#555",margin:"4px 0 15px 0",fontSize:"0.85rem"}}>Ready to analyze</p>
                  <button onClick={function(){ setVideo(null); }} style={{padding:"8px 16px",background:"transparent",color:"#555",border:"1px solid rgba(255,255,255,0.08)",borderRadius:"8px",cursor:"pointer",fontFamily:"Inter, sans-serif",fontSize:"0.85rem"}}>Choose different video</button>
                </div>
              )}
            </div>
          )}

          {mode === "record" && (
            <div style={{background:"rgba(255,255,255,0.02)",padding:"25px",borderRadius:"16px",border:"1px solid rgba(255,255,255,0.06)"}}>
              {!video ? (
                <>
                  <div style={{position:"relative",borderRadius:"12px",overflow:"hidden",marginBottom:"20px",background:"#000",aspectRatio:"16/9"}}>
                    <video ref={videoRef} muted playsInline style={{width:"100%",height:"100%",objectFit:"cover"}} />
                    {!cameraReady && (
                      <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:"10px"}}>
                        <div style={{fontSize:"2rem"}}>📷</div>
                        <p style={{color:"#666",margin:0,fontSize:"0.9rem"}}>Camera loading...</p>
                      </div>
                    )}
                    {recording && (
                      <div style={{position:"absolute",top:"15px",left:"15px",display:"flex",alignItems:"center",gap:"8px",background:"rgba(0,0,0,0.7)",borderRadius:"20px",padding:"6px 14px"}}>
                        <div style={{width:"8px",height:"8px",borderRadius:"50%",background:"#e94560",animation:"blink 1s ease-in-out infinite"}}></div>
                        <span style={{color:"white",fontSize:"0.85rem",fontWeight:"600"}}>{recordingTime}s / {MAX_RECORD_SECONDS}s</span>
                      </div>
                    )}
                  </div>
                  <div style={{textAlign:"center"}}>
                    <p style={{color:"#555",fontSize:"0.8rem",marginBottom:"20px"}}>{recording ? "Tap to stop recording" : "Tap to start recording • Max 60 seconds"}</p>
                    <div style={{position:"relative",display:"inline-block"}}>
                      {recording && (
                        <div style={{position:"absolute",top:"-8px",left:"-8px",right:"-8px",bottom:"-8px",borderRadius:"50%",border:"3px solid rgba(233,69,96,0.4)",animation:"pulse-ring 1.5s ease-out infinite"}}></div>
                      )}
                      <button
                        onClick={function(){ recording ? stopRecording() : startRecording(); }}
                        disabled={!cameraReady}
                        style={{width:"80px",height:"80px",borderRadius:"50%",background: recording ? "linear-gradient(135deg, #e94560, #ff6b35)" : cameraReady ? "rgba(233,69,96,0.15)" : "rgba(255,255,255,0.05)",border: recording ? "none" : "3px solid " + (cameraReady ? "#e94560" : "#333"),cursor: cameraReady ? "pointer" : "not-allowed",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"2rem",boxShadow: recording ? "0 0 30px rgba(233,69,96,0.5)" : "none",transition:"all 0.2s"}}>
                        {recording ? "⏹" : "⏺"}
                      </button>
                    </div>
                    <p style={{color:"#333",fontSize:"0.75rem",marginTop:"15px"}}>💡 Prop your phone up or have a partner film you</p>
                  </div>
                </>
              ) : (
                <div>
                  <p style={{color:"#888",fontSize:"0.85rem",marginBottom:"12px",textTransform:"uppercase",letterSpacing:"1px"}}>Preview Your Recording</p>
                  <video src={previewUrl} controls style={{width:"100%",borderRadius:"12px",marginBottom:"15px",maxHeight:"300px"}} />
                  <button onClick={resetAll} style={{width:"100%",padding:"12px",background:"rgba(255,255,255,0.04)",color:"#666",border:"1px solid rgba(255,255,255,0.08)",borderRadius:"10px",cursor:"pointer",fontFamily:"Inter, sans-serif",fontSize:"0.9rem",marginBottom:"10px"}}>
                    Record Again
                  </button>
                </div>
              )}
            </div>
          )}

          {error && (
            <div style={{background:"rgba(233,69,96,0.08)",padding:"16px",borderRadius:"12px",marginTop:"15px",border:"1px solid rgba(233,69,96,0.2)"}}>
              <p style={{color:"#e94560",margin:0,fontSize:"0.9rem"}}>⚠️ {error}</p>
            </div>
          )}

          <button onClick={handleUpload} disabled={!video || uploading} style={{width:"100%",padding:"16px",marginTop:"15px",background: !video||uploading ? "rgba(255,255,255,0.05)" : "linear-gradient(135deg, #e94560, #ff6b35)",color: !video||uploading ? "#444" : "white",border:"none",borderRadius:"12px",fontSize:"1rem",fontWeight:"700",fontFamily:"Inter, sans-serif",cursor: !video||uploading ? "not-allowed" : "pointer",letterSpacing:"1px",textTransform:"uppercase",transition:"all 0.2s"}}>
            {uploading ? "Analyzing..." : "Analyze Technique"}
          </button>
        </>
      ) : null}

      {uploading && progressMsg && (
        <div style={{background:"rgba(255,255,255,0.02)",padding:"30px",borderRadius:"16px",textAlign:"center",border:"1px solid rgba(255,255,255,0.06)",marginTop:"20px"}}>
          <div style={{fontSize:"2.5rem",marginBottom:"15px"}}>🤖</div>
          <p style={{color:"#888",fontSize:"1rem",margin:0}}>{progressMsg}</p>
        </div>
      )}

      {reward && (
        <div style={{background:"linear-gradient(135deg, rgba(76,175,80,0.1), rgba(76,175,80,0.05))",padding:"20px 25px",borderRadius:"16px",marginTop:"15px",border:"1px solid rgba(76,175,80,0.2)",textAlign:"center"}}>
          <p style={{color:"#4caf50",fontSize:"1.3rem",fontWeight:"700",margin:"0 0 8px 0"}}>+{reward.xpEarned} XP Earned!</p>
          {reward.stats && reward.stats.streak > 1 && <p style={{color:"#ff9800",margin:"0 0 8px 0"}}>🔥 {reward.stats.streak} Day Streak!</p>}
          {reward.newBadges && reward.newBadges.map(function(badgeId) {
            var badge = BADGES[badgeId];
            if (!badge) return null;
            return (<div key={badgeId} style={{display:"inline-flex",alignItems:"center",gap:"8px",background:"rgba(233,69,96,0.1)",border:"1px solid rgba(233,69,96,0.3)",padding:"8px 18px",borderRadius:"20px",marginTop:"8px"}}><span>{badge.emoji}</span><span style={{color:"white",fontWeight:"700"}}>Badge Unlocked: {badge.name}</span></div>);
          })}
        </div>
      )}

      {feedback && feedback !== "analyzing" && (
        <div style={{marginTop:"20px"}}>
          {analysis && analysis.summary && (
            <div style={{background:"linear-gradient(135deg, rgba(233,69,96,0.12), rgba(255,107,53,0.06))",padding:"20px 25px",borderRadius:"14px",marginBottom:"20px",border:"1px solid rgba(233,69,96,0.25)"}}>
              <p style={{color:"#e94560",margin:"0 0 5px 0",fontSize:"0.75rem",textTransform:"uppercase",letterSpacing:"1px",fontWeight:"700"}}>⚡ Biggest Issue</p>
              <p style={{color:"white",margin:0,fontSize:"1rem",lineHeight:"1.6"}}>{analysis.summary}</p>
            </div>
          )}

          <div style={{display:"flex",gap:"8px",marginBottom:"20px"}}>
            <button onClick={function(){ setActiveTab("visual"); }} style={{padding:"10px 20px",borderRadius:"10px",cursor:"pointer",fontFamily:"Inter, sans-serif",fontSize:"0.9rem",fontWeight:"600",border: activeTab==="visual" ? "none" : "1px solid rgba(255,255,255,0.08)",background: activeTab==="visual" ? "linear-gradient(135deg, #e94560, #ff6b35)" : "rgba(255,255,255,0.03)",color: activeTab==="visual" ? "white" : "#666"}}>
              🫀 Body Analysis
            </button>
            <button onClick={function(){ setActiveTab("text"); }} style={{padding:"10px 20px",borderRadius:"10px",cursor:"pointer",fontFamily:"Inter, sans-serif",fontSize:"0.9rem",fontWeight:"600",border: activeTab==="text" ? "none" : "1px solid rgba(255,255,255,0.08)",background: activeTab==="text" ? "linear-gradient(135deg, #e94560, #ff6b35)" : "rgba(255,255,255,0.03)",color: activeTab==="text" ? "white" : "#666"}}>
              📋 Full Feedback
            </button>
          </div>

          {activeTab === "visual" && analysis && (
            <div style={{background:"rgba(255,255,255,0.02)",padding:"25px",borderRadius:"16px",border:"1px solid rgba(255,255,255,0.06)"}}>
              <BodyFeedback analysis={analysis} />
            </div>
          )}

          {activeTab === "text" && (
            <div style={{background:"linear-gradient(135deg, rgba(233,69,96,0.08), rgba(255,107,53,0.04))",padding:"30px",borderRadius:"16px",border:"1px solid rgba(233,69,96,0.2)"}}>
              <h3 style={{fontFamily:"Bebas Neue, sans-serif",fontSize:"1.8rem",letterSpacing:"2px",background:"linear-gradient(135deg, #e94560, #ff6b35)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",marginBottom:"20px"}}>AI COACH FEEDBACK</h3>
              <div style={{color:"#ccc",lineHeight:"1.9",whiteSpace:"pre-wrap",fontSize:"0.95rem"}}>{feedback}</div>
            </div>
          )}

          <button onClick={resetAll} style={{marginTop:"20px",width:"100%",padding:"14px",background:"rgba(255,255,255,0.03)",color:"#666",border:"1px solid rgba(255,255,255,0.08)",borderRadius:"12px",cursor:"pointer",fontFamily:"Inter, sans-serif",fontSize:"0.9rem"}}>
            + Analyze Another Video
          </button>
        </div>
      )}
    </div>
  );
}

export default VideoUpload;
